import { useRef, type FC } from "react";

import { Button } from "@/shared/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "@/shared/components/ui/sheet";
import { useContactForm } from "@/shared/hooks/useContactForm";
import { useEstateContactMutations } from "../hooks/useEstateContactMutations";
import { buildAddress } from "@/shared/utils/addressUtils";
import { resolveAddressLink } from "@/shared/utils/resolveAddressLink";
import { splitName } from "@/shared/utils/names";
import { saveProfilePicture } from "@/shared/utils/saveProfilePicture";

import ContactFormFields from "@/shared/components/Contact/ContactFormFields";
import { getRelationshipLabel, getDeceasedFirstName } from "@/shared/utils/contactLabels";

interface LawyerAddContactSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  estateId: number;
  deceasedUserData?: { displayName?: string; firstName?: string } | null;
}

export const LawyerAddContactSheet: FC<LawyerAddContactSheetProps> = ({
  isOpen,
  onOpenChange,
  estateId,
  deceasedUserData,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const { createContactAsync, isCreating } = useEstateContactMutations(estateId);

  const {
    formData,
    errors,
    setErrors,
    isSubmitting,
    setIsSubmitting,
    profilePictureUrl,
    selectedFile,
    validateForm,
    handleInputChange,
    handleAddressChange,
    handleProfilePictureSelect,
    uploadProfilePicture,
    resetForm,
  } = useContactForm(estateId);

  const deceasedFirstName = getDeceasedFirstName(deceasedUserData);
  const relationshipLabel = getRelationshipLabel(formData.fullName, deceasedUserData);

  const handleSave = async () => {
    if (!validateForm()) return;
    setIsSubmitting(true);

    try {
      const { firstName, surName } = splitName(formData.fullName);

      const legalRoleValues = formData.legalRole
        .map((role) => role.value)
        .filter((value) => value !== "none");

      let profilePictureKey: string | null = null;
      if (selectedFile) {
        try {
          profilePictureKey = await uploadProfilePicture(selectedFile);
        } catch (error) {
          console.error("Profile picture upload failed; saving contact without picture", error);
        }
      }

      const addressString = buildAddress(formData.address);

      const savedContact = await createContactAsync({
        first_name: firstName,
        last_name: surName,
        legal_role: legalRoleValues,
        relationship: formData.relationship || null,
        email: formData.email || null,
        phone: formData.phone || null,
        address: addressString || null,
        profile_picture: profilePictureKey,
      });

      if (savedContact) {
        void resolveAddressLink({
          raw: addressString,
          purpose: "mailing",
          entity: { contact_id: savedContact.id },
        });
      }

      if (profilePictureKey && savedContact) {
        await saveProfilePicture({
          fileKey: profilePictureKey,
          contactId: savedContact.id,
          userId: savedContact.user_id,
        });
      }

      onOpenChange(false);
      resetForm();
    } catch (error) {
      if (error instanceof Error && error.message.includes("email already exists")) {
        setErrors({ email: "A contact with this email already exists" });
      } else {
        console.error("Failed to save contact:", error);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onOpenChange}>
      <SheetContent
        ref={sheetRef}
        side="right"
        className="w-full sm:w-1/2 sm:max-w-[50vw] p-0 overflow-y-auto bg-white"
      >
        <SheetHeader className="flex flex-col bg-transparent border-b-0 px-[16px] sm:px-[32px] pt-[24px] pb-[16px]">
          <div className="flex items-center gap-3 pr-10">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="relative size-12 rounded-full bg-brand flex items-center justify-center text-white font-semibold overflow-hidden hover:opacity-90 transition-opacity shrink-0"
              aria-label="Add profile picture"
            >
              {profilePictureUrl ? (
                <img
                  src={profilePictureUrl}
                  alt={formData.fullName || "Profile"}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-base">
                  {formData.fullName
                    .split(" ")
                    .map((n) => n[0])
                    .filter(Boolean)
                    .slice(0, 2)
                    .join("")
                    .toUpperCase() || "+"}
                </span>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleProfilePictureSelect}
            />
            <SheetTitle className="text-xl font-semibold leading-tight font-sans">
              {formData.fullName || "New contact"}
            </SheetTitle>
          </div>
        </SheetHeader>
        <div className="px-[16px] sm:px-[32px] pb-0 pt-0">
          <ContactFormFields
            formData={formData}
            errors={errors}
            onInputChange={handleInputChange}
            onAddressChange={handleAddressChange}
            relationshipLabel={relationshipLabel}
            deceasedFirstName={deceasedFirstName}
            isMeContact={false}
            portalContainer={sheetRef.current}
            legalRoleEditable
          />
        </div>
        <SheetFooter className="flex justify-end px-[16px] sm:px-[32px] py-[24px]">
          <Button
            onClick={handleSave}
            disabled={isSubmitting || isCreating}
            variant="brand"
            className="md:w-fit"
          >
            {isSubmitting || isCreating ? "Saving..." : "Save"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};
